import React, { useState } from 'react';
import { AIAgentCapabilityConfig } from '@troia-v3/types';
import { Input } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

interface MediaMessagingConfigProps {
  config: AIAgentCapabilityConfig | undefined;
  onChange: (config: AIAgentCapabilityConfig) => void;
}

// Mesma regra do backend (ai-agents/validation.ts): http(s):// + host +
// barra após o host. Sem a barra, o prefixo seria vulnerável a bypass de
// sufixo de domínio (https://cdn.x.com casaria com https://cdn.x.com.evil.net/...).
const PREFIX_REGEX = /^https?:\/\/[^/\s]+\/\S*$/;

export const MediaMessagingConfig: React.FC<MediaMessagingConfigProps> = ({
  config,
  onChange
}) => {
  // Ausente = habilitado: agentes criados antes da flag continuam enviando
  // mídia normalmente (mesma semântica do backend — retrocompat).
  const isEnabled = config ? config.enabled : true;
  const prefixes = config?.config?.allowedMediaUrlPrefixes || [];

  const [newPrefix, setNewPrefix] = useState('');
  const [prefixError, setPrefixError] = useState('');

  const emit = (enabled: boolean, nextPrefixes: string[]) => {
    onChange({
      capability: 'media_messaging',
      enabled,
      config: {
        ...config?.config,
        allowedMediaUrlPrefixes: nextPrefixes
      }
    });
  };

  const handleToggleEnabled = (enabled: boolean) => {
    emit(enabled, prefixes);
  };

  const handleAddPrefix = () => {
    const value = newPrefix.trim();
    if (!value) return;
    if (!PREFIX_REGEX.test(value)) {
      setPrefixError('Prefixo inválido. Use http(s):// com barra após o domínio (ex: https://cdn.exemplo.com/r/)');
      return;
    }
    if (prefixes.includes(value)) {
      setPrefixError('Prefixo já adicionado');
      return;
    }
    setPrefixError('');
    setNewPrefix('');
    emit(isEnabled, [...prefixes, value]);
  };

  const handleRemovePrefix = (prefix: string) => {
    emit(isEnabled, prefixes.filter(p => p !== prefix));
  };

  return (
    <div className="space-y-4">
      {/* Header com Toggle */}
      <div
        className="p-4 rounded-lg border"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: 'var(--theme-primary-background)',
              color: 'var(--theme-primary)',
            }}
          >
            <Icon name="image" className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h4
                className="text-sm font-semibold"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Envio de mídia
              </h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="media-messaging-toggle"
                  checked={isEnabled}
                  onChange={(e) => handleToggleEnabled(e.target.checked)}
                  className="w-4 h-4"
                  style={{ accentColor: 'var(--theme-primary)' }}
                />
                <span
                  className="text-xs font-medium"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  {isEnabled ? 'Habilitado' : 'Desabilitado'}
                </span>
              </label>
            </div>
            <p
              className="text-xs"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Permite que o agente envie imagens, vídeos e documentos durante as conversas
            </p>
          </div>
        </div>
      </div>

      {/* Configurações (apenas se habilitado) */}
      {isEnabled && (
        <div className="space-y-4">
          <div>
            <label
              className="block text-xs mb-2 font-medium"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Prefixos de URL autorizados
            </label>
            <p
              className="text-xs mb-3"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              URLs que aparecem no contexto da conversa sempre funcionam. Adicione prefixos para liberar URLs dinâmicas — qualquer URL que comece com o prefixo será aceita (ex: https://tempfile.aiquickdraw.com/r/).
            </p>

            <div className="flex gap-2">
              <Input
                data-testid="media-messaging-prefix-input"
                type="text"
                value={newPrefix}
                onChange={(e) => {
                  setNewPrefix(e.target.value);
                  setPrefixError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddPrefix();
                  }
                }}
                placeholder="https://cdn.exemplo.com/r/"
                {...(prefixError ? { error: prefixError } : {})}
              />
              <button
                type="button"
                data-testid="media-messaging-add-prefix"
                onClick={handleAddPrefix}
                className="px-3 py-2 rounded-lg text-sm font-medium flex-shrink-0 self-start"
                style={{
                  backgroundColor: 'var(--theme-button-primary)',
                  color: 'var(--theme-button-primary-text)',
                }}
              >
                Adicionar
              </button>
            </div>

            {prefixes.length > 0 && (
              <div className="mt-3 space-y-2">
                {prefixes.map((prefix) => (
                  <div
                    key={prefix}
                    data-testid={`media-messaging-prefix-item-${prefix}`}
                    className="flex items-center justify-between px-3 py-2 rounded-lg border"
                    style={{
                      backgroundColor: 'var(--theme-input-background)',
                      borderColor: 'var(--theme-input-border)',
                      color: 'var(--theme-input-text)',
                    }}
                  >
                    <span className="truncate font-mono text-xs">{prefix}</span>
                    <button
                      type="button"
                      data-testid={`media-messaging-remove-prefix-${prefix}`}
                      onClick={() => handleRemovePrefix(prefix)}
                      className="p-1 rounded flex-shrink-0 ml-2"
                      style={{ color: 'var(--theme-muted-text)' }}
                      title="Remover prefixo"
                    >
                      <Icon name="x" className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
